// src/components/icons/MenuIcons.tsx
/**
 * Tập trung quản lý tất cả các icon sử dụng trong ứng dụng
 * Nguồn icon từ nhiều thư viện: FontAwesome, Material Design, Heroicons
 */
import React from 'react';
import { IconType } from 'react-icons';

// ============= ICON IMPORTS =============

// Font Awesome Icons
import {
    // Navigation
    FaHome, FaCompass, FaUsers, FaBell, FaChevronDown, FaChevronUp, FaUser,
    FaChevronRight, FaChevronLeft, FaStream, FaBars as Menu, FaEllipsisV, FaClock,

    // Document & Content
    FaFolder, FaFileAlt, FaBook, FaFileDownload, FaFileContract,
    FaImage, FaVideo, FaMusic, FaGlobeAmericas, FaEye,

    // Actions
    FaPlus, FaEdit, FaPencilAlt, FaTrashAlt, FaList, FaSearch,
    FaDownload, FaUpload, FaCheck, FaBan, FaTimes, FaFilter,
    FaStar, FaThumbsUp, FaExchangeAlt,

    // Finance & Payment
    FaDollarSign, FaCreditCard, FaShoppingCart,

    // Protection & Security
    FaShieldAlt, FaUserShield, FaLock, FaUnlock, FaExclamationTriangle,
    FaExclamationCircle, FaGavel, FaBalanceScale, FaRegCopyright,

    // Analytics & Admin
    FaChartBar, FaCog, FaUserCog, FaHistory, FaGlobe, FaGem,

    // Communication
    FaEnvelope, FaPhone, FaComments, FaMobileAlt, FaBellSlash,
    FaInbox, FaExternalLinkAlt, FaCalendarAlt,

    // Status & Loading
    FaSync, FaRedo, FaSpinner as Loading,

    // Education
    FaGraduationCap,
    FaQuestionCircle,
    FaSort,

    // Additional icons for Teacher components
    FaIdCard, FaBuilding, FaUniversity, FaPercent,
    FaChartLine, FaMobileAlt as FaMobile, FaFileInvoice,
    FaCloudUploadAlt, FaFileExport,

    // File types - MISSING ICONS ADDED
    FaFilePdf, FaFileWord, FaFileImage, FaFile,

    // User status icons - MISSING ICONS ADDED
    FaUserCheck, FaUserTimes, FaTimesCircle, FaCheckCircle,
    FaHeadset, FaCrown, FaHandshake,

    // --------- Certificate ---------

    FaCertificate, // Thêm icon chứng chỉ
    FaStamp, // Icon cho giấy phép chính thức    
    FaFlag, // Icon cho cục SHTT
    FaLink, // Icon cho integration    
    FaQrcode, // Icon cho QR code
    FaInfoCircle
} from 'react-icons/fa';

// Material Design Icons
import {
    // Notifications & Alerts
    MdWarning, MdError, MdErrorOutline, MdPriorityHigh, MdInfo, MdNotifications,

    // Actions & Navigation
    MdAutorenew, MdRefresh, MdLanguage, MdPublic, MdRemoveRedEye,

    // Analytics & Admin
    MdTrendingUp, MdArrowUpward, MdArrowDownward, MdSettings, MdDashboard,

    // Finance & Payment
    MdPayment,

    // Security
    MdSecurity,

    // Education
    MdSchool, MdVideoLibrary,

    // Media
    MdImage, MdMusicNote, MdWork,

    // MISSING MATERIAL DESIGN ICONS ADDED
    MdTrendingDown, MdArrowForward, MdAccountBalance,
    MdFileDownload, MdVerified, MdBlock, MdCompareArrows,

    // Certificate
    MdVerifiedUser, // Icon cho verification
    MdAssignment, // Icon cho assignment
    MdGavel, // Icon cho legal
    MdLogout,
    MdLogin
} from 'react-icons/md';

// Heroicons
import {
    // Navigation
    ChevronRightIcon, ChevronLeftIcon,

    // Analytics & Charts
    ChartBarIcon, ChartPieIcon, ArrowTrendingUpIcon,

    // Document & Content
    DocumentTextIcon, BookOpenIcon, PhotoIcon, VideoCameraIcon,
    MusicalNoteIcon, GlobeAltIcon, EyeIcon as Eye,

    // User & Admin
    UsersIcon, Cog6ToothIcon,

    // Finance
    CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

// Other Icon Libraries
import { GoFileDirectory as FileSize } from 'react-icons/go';
import { BsGlobe as Public, BsLock as Lock } from 'react-icons/bs';

// ============= INTERFACES =============

interface ColoredIconProps {
    Icon: IconType;
    color: string;
    className?: string;
    size?: number | string;
}

export interface IconProps {
    className?: string;
    size?: number | string;
    color?: string;
    onClick?: () => void;
}

// ============= CENTRAL ICON COLLECTION =============

/**
 * Tổng hợp tất cả icon trong ứng dụng để sử dụng thống nhất
 * Phân loại theo nhóm chức năng: Navigation, Document, Action, v.v.
 */
export const Icons = {
    // --------- Navigation & UI Elements ---------
    Home: FaHome,
    Explore: FaCompass,
    Community: FaUsers,
    Notification: FaBell,
    ChevronDown: FaChevronDown,
    ChevronUp: FaChevronUp,
    ChevronRight: FaChevronRight,
    ChevronLeft: FaChevronLeft,
    ChevronRightHero: ChevronRightIcon,
    ChevronLeftHero: ChevronLeftIcon,
    ChevronLeftIcon: ChevronLeftIcon, // Alias để tương thích với code cũ
    Filter: FaFilter,
    EllipsisV: FaEllipsisV,
    Clock: FaClock,
    Stream: FaStream,
    Language: MdLanguage,
    Menu: Menu,
    Logout: MdLogout,
    Login: MdLogin,

    // --------- Documents & Content ---------
    Folder: FaFolder,
    File: FaFileAlt,
    Document: DocumentTextIcon,
    FileDownload: FaFileDownload,
    Book: BookOpenIcon,
    FileAlt: FaFileAlt,
    Contract: FaFileContract,

    // File types - ADDED MISSING FILE TYPE ICONS
    FaFilePdf: FaFilePdf,
    FaFileWord: FaFileWord,
    FaFileImage: FaFileImage,
    FaFile: FaFile,

    // Media Types
    Image: FaImage,
    ImageHero: PhotoIcon,
    ImageMaterial: MdImage,
    Video: FaVideo,
    VideoHero: VideoCameraIcon,
    VideoMaterial: MdVideoLibrary,
    Music: FaMusic,
    MusicHero: MusicalNoteIcon,
    MusicMaterial: MdMusicNote,

    // Visibility controls
    Public: FaGlobeAmericas,
    PublicHero: GlobeAltIcon,
    PublicMaterial: MdPublic,
    Eye: FaEye,
    EyeHero: FaEye,
    EyeMaterial: MdRemoveRedEye,
    Star: FaStar,
    ThumbUp: FaThumbsUp,

    // --------- Actions ---------
    Add: FaPlus,
    Edit: FaEdit,
    EditPencil: FaPencilAlt,
    Delete: FaTrashAlt,
    List: FaList,
    Search: FaSearch,
    Download: FaDownload,
    Upload: FaUpload,
    Check: FaCheck,
    Ban: FaBan,
    Times: FaTimes,
    FileSize: FileSize,
    Calendar: FaCalendarAlt,

    // Status icons - ADDED MISSING STATUS ICONS
    CheckCircle: FaCheckCircle,
    TimesCircle: FaTimesCircle,

    // --------- Finance & Payment ---------
    Money: FaDollarSign,
    Currency: CurrencyDollarIcon,
    CreditCard: FaCreditCard,
    Exchange: FaExchangeAlt,
    Payment: MdPayment,
    ShoppingCart: FaShoppingCart,
    Percent: FaPercent, // ADDED MISSING PERCENT ICON

    Headset: FaHeadset,
    Crown: FaCrown,
    Handshake: FaHandshake,


    // Material Design specific finance icons - ADDED MISSING
    MdAccountBalance: MdAccountBalance,
    MdFileDownload: MdFileDownload,

    // --------- Protection & Security ---------
    Shield: FaShieldAlt,
    UserShield: FaUserShield,
    Lock: FaLock,
    Unlock: FaUnlock,
    Security: MdSecurity,

    // --------- Legal ---------
    Copyright: FaRegCopyright,
    Gavel: FaGavel,
    Scale: FaBalanceScale,

    // --------- Analytics & Admin ---------
    ChartBar: FaChartBar,
    Analytics: ChartBarIcon,
    ChartPie: ChartPieIcon,
    Trend: ArrowTrendingUpIcon,
    TrendingUp: MdTrendingUp,
    ArrowUpward: MdArrowUpward,
    ArrowDownward: MdArrowDownward,
    //Settings: MdSettings,
    TrendingDown: MdTrendingDown, // ADDED MISSING TRENDING DOWN
    Settings: FaCog,
    Cog: FaCog,
    Cog6Tooth: Cog6ToothIcon,
    UserConfig: FaUserCog,
    History: FaHistory,
    Global: FaGlobe,
    Dashboard: MdDashboard,
    Premium: FaGem,
    ChartLine: FaChartLine, // ADDED MISSING CHART LINE

    // --------- Warnings & Info ---------
    Warning: FaExclamationTriangle,
    Error: MdError,
    ErrorOutline: MdErrorOutline,
    ExclamationCircle: FaExclamationCircle,
    Info: MdInfo,
    Question: FaQuestionCircle,
    PriorityHigh: MdPriorityHigh,

    // Material Design specific - ADDED MISSING
    MdWarning: MdWarning,
    MdVerified: MdVerified,
    MdBlock: MdBlock,
    MdArrowForward: MdArrowForward,
    CompareArrows: MdCompareArrows,

    // --------- Users ---------
    User: FaUser,
    Users: UsersIcon,

    // User status icons - ADDED MISSING USER ICONS
    UserCheck: FaUserCheck,
    UserTimes: FaUserTimes,

    // --------- Notifications ---------
    Bell: FaBell,
    BellSlash: FaBellSlash,
    Notifications: MdNotifications,

    // --------- Loading & Status ---------
    Refresh: FaSync,
    Redo: FaRedo,
    Autorenew: MdAutorenew,
    MdRefresh: MdRefresh,
    Loading: Loading,

    // --------- Communication ---------
    Envelope: FaEnvelope,
    Phone: FaPhone,
    Comments: FaComments,
    MobileAlt: FaMobileAlt,
    Inbox: FaInbox,
    ExternalLink: FaExternalLinkAlt,

    // --------- Education ---------
    School: MdSchool,
    VideoLibrary: MdVideoLibrary,
    GraduationCap: FaGraduationCap,

    // File management
    FileExport: FaFileExport,
    Sort: FaSort,

    // Material Design specific
    MdWork: MdWork,

    // --------- Professional & Institution ---------
    IdCard: FaIdCard,
    Building: FaBuilding,
    University: FaUniversity,
    FileInvoice: FaFileInvoice,

    // --------- File Management ---------
    CloudUpload: FaCloudUploadAlt,

    // --------- Certificate ---------

    FaCertificate, // Thêm icon chứng chỉ
    FaStamp, // Icon cho giấy phép chính thức    
    FaFlag, // Icon cho cục SHTT
    FaLink, // Icon cho integration    
    FaQrcode, // Icon cho QR code
    FaInfoCircle
};

// ============= COLORED ICON COMPONENTS =============

/**
 * Component để tạo icon có màu sắc
 */
export const ColoredIcon: React.FC<ColoredIconProps> = ({ Icon, color, className, size }) => {
    return <Icon style={{ color }} className={className} size={size} />;
};

// Preset Colored Icons cho các icon thường dùng
export const HomeIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Home} color="#4f46e5" {...props} />;
export const ExploreIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Explore} color="#0891b2" {...props} />;
export const CommunityIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Community} color="#2563eb" {...props} />;
export const NotificationIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Notification} color="#db2777" {...props} />;

// Content Management Icons
export const FolderIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Folder} color="#059669" {...props} />;
export const FileIcon = (props: IconProps) => <ColoredIcon Icon={Icons.File} color="#6366f1" {...props} />;
export const AddIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Add} color="#10b981" {...props} />;
export const EditIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Edit} color="#f59e0b" {...props} />;
export const DeleteIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Delete} color="#ef4444" {...props} />;
export const ListIcon = (props: IconProps) => <ColoredIcon Icon={Icons.List} color="#8b5cf6" {...props} />;
export const SearchIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Search} color="#6b7280" {...props} />;
export const EditDocIcon = (props: IconProps) => <ColoredIcon Icon={Icons.EditPencil} color="#9333ea" {...props} />;

// Status Icons - ADDED MISSING COLORED COMPONENTS
export const CheckCircleIcon = (props: IconProps) => <ColoredIcon Icon={Icons.CheckCircle} color="#10b981" {...props} />;
export const TimesCircleIcon = (props: IconProps) => <ColoredIcon Icon={Icons.TimesCircle} color="#ef4444" {...props} />;
export const ClockIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Clock} color="#f59e0b" {...props} />;

// User Icons - ADDED MISSING COLORED COMPONENTS
export const UserIcon = (props: IconProps) => <ColoredIcon Icon={Icons.User} color="#6366f1" {...props} />;
export const UserCheckIcon = (props: IconProps) => <ColoredIcon Icon={Icons.UserCheck} color="#10b981" {...props} />;
export const UserTimesIcon = (props: IconProps) => <ColoredIcon Icon={Icons.UserTimes} color="#ef4444" {...props} />;

// Action Icons - ADDED MISSING COLORED COMPONENTS
export const EyeIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Eye} color="#3b82f6" {...props} />;
export const DownloadIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Download} color="#1d4ed8" {...props} />;
export const UploadIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Upload} color="#4f46e5" {...props} />;
export const TrashIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Delete} color="#ef4444" {...props} />;
export const BanIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Ban} color="#dc2626" {...props} />;
export const PlusIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Add} color="#10b981" {...props} />;

// Filter and Sort Icons - ADDED MISSING COLORED COMPONENTS
export const FilterIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Filter} color="#6b7280" {...props} />;
export const SortIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Sort} color="#6b7280" {...props} />;

// Finance Icons
export const MoneyIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Money} color="#047857" {...props} />;
export const CreditCardIcon = (props: IconProps) => <ColoredIcon Icon={Icons.CreditCard} color="#0369a1" {...props} />;
export const ExchangeIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Exchange} color="#0d9488" {...props} />;

// Education Icons - ADDED MISSING COLORED COMPONENTS
export const BookIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Book} color="#1d4ed8" {...props} />;
export const GraduationCapIcon = (props: IconProps) => <ColoredIcon Icon={Icons.GraduationCap} color="#7c3aed" {...props} />;
export const SchoolIcon = (props: IconProps) => <ColoredIcon Icon={Icons.School} color="#3b82f6" {...props} />;
export const UniversityIcon = (props: IconProps) => <ColoredIcon Icon={Icons.University} color="#4338ca" {...props} />;

// Communication Icons - ADDED MISSING COLORED COMPONENTS
export const EnvelopeIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Envelope} color="#6366f1" {...props} />;
export const PhoneIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Phone} color="#059669" {...props} />;

// Document specific icons - ADDED MISSING COLORED COMPONENTS
export const DocumentIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Document} color="#4b5563" {...props} />;
export const IdCardIcon = (props: IconProps) => <ColoredIcon Icon={Icons.IdCard} color="#7c3aed" {...props} />;

// Calendar and time - ADDED MISSING COLORED COMPONENTS
export const CalendarIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Calendar} color="#f59e0b" {...props} />;

// File export - ADDED MISSING COLORED COMPONENTS
export const FileExportIcon = (props: IconProps) => <ColoredIcon Icon={Icons.FileExport} color="#059669" {...props} />;

// Protection Icons
export const ShieldIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Shield} color="#4338ca" {...props} />;
export const UserShieldIcon = (props: IconProps) => <ColoredIcon Icon={Icons.UserShield} color="#4f46e5" {...props} />;
export const LockIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Lock} color="#9333ea" {...props} />;
export const UnlockIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Unlock} color="#15803d" {...props} />;
export const WarningIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Warning} color="#d97706" {...props} />;

// Admin Icons
export const SettingsIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Settings} color="#6366f1" {...props} />;
export const UserConfigIcon = (props: IconProps) => <ColoredIcon Icon={Icons.UserConfig} color="#7c3aed" {...props} />;
export const HistoryIcon = (props: IconProps) => <ColoredIcon Icon={Icons.History} color="#6b7280" {...props} />;
export const GlobalIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Global} color="#2563eb" {...props} />;

// Legal Icons
export const CopyrightIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Copyright} color="#8b5cf6" {...props} />;
export const ContractIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Contract} color="#0284c7" {...props} />;
export const GavelIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Gavel} color="#b91c1c" {...props} />;
export const ScaleIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Scale} color="#4b5563" {...props} />;
export const CheckIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Check} color="#16a34a" {...props} />;

// Star Icon - ADDED MISSING COLORED COMPONENT
export const StarIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Star} color="#f59e0b" {...props} />;

// Miscellaneous
export const ChartIcon = (props: IconProps) => <ColoredIcon Icon={Icons.ChartBar} color="#3b82f6" {...props} />;
export const QuestionIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Question} color="#6b7280" {...props} />;
export const PremiumIcon = (props: IconProps) => <ColoredIcon Icon={Icons.Premium} color="#8b5cf6" {...props} />;


export const CertificateIcons = {
    // Main certificate icon
    Certificate: FaCertificate,

    // Organization types
    Vietrro: FaStamp,
    Wipo: FaGlobe,
    Noip: FaFlag,
    Association: FaHandshake,

    // Process icons
    Application: FaFileAlt,
    Tracking: FaSearch,
    Approval: FaCheck,
    History: FaHistory,

    // Integration icons
    Integration: FaLink,
    Organization: FaUniversity,

    // Security & Verification
    Verification: MdVerifiedUser,
    QrCode: FaQrcode,
    InfoCircle: FaInfoCircle,
    Blockchain: FaLock,
    Security: FaShieldAlt,

    // Legal & Assignment
    Assignment: MdAssignment,
    Legal: MdGavel
};

// ============= UTILITY FUNCTIONS =============

/**
 * Map đường dẫn URL tới icon component
 * Sử dụng để hiển thị icon tương ứng với mỗi route
 */
export const PATH_TO_ICON_MAP: Record<string, React.FC<IconProps>> = {
    // Top level paths
    '/': HomeIcon,
    '/explore': ExploreIcon,
    '/community': CommunityIcon,
    '/notifications': NotificationIcon,

    // Rights Owner paths
    '/content/create': AddIcon,
    '/catalog/manage': FolderIcon,
    '/catalog/metadata': FileIcon,
    '/catalog/view': EditDocIcon,
    '/rights/revenue-dashboard': ChartIcon,
    '/rights/payments/withdraw': MoneyIcon,
    '/violations/report': WarningIcon,
    '/identity-protection/dashboard': ShieldIcon,

    // Licensee paths
    '/public/works': SearchIcon,
    '/licenses/purchase': CreditCardIcon,
    '/licenses/payments': MoneyIcon,
    '/licenses/contracts': ContractIcon,
    '/licenses/contracts/manage': EditIcon,

    // Admin paths
    '/admin/system': SettingsIcon,
    '/admin/permissions': UserConfigIcon,
    '/admin/activity-logs': HistoryIcon,
    '/partners/api': GlobalIcon,

    // Monitor paths
    '/monitoring/ai-scan': SearchIcon,
    '/monitoring/warnings': WarningIcon,
    '/monitoring/revocation': BanIcon,

    // Legal paths
    '/legal/disputes': ScaleIcon,
    '/legal/violations': GavelIcon,
    '/legal/enforcement': LockIcon,

    // Education paths
    '/education/dashboard': HomeIcon,
    '/education/documents': FileIcon,
    '/education/help': QuestionIcon,
    '/education/notifications': NotificationIcon,
    '/education/subscription': PremiumIcon,
    '/education/revenue': MoneyIcon,
    '/education/analytics': ChartIcon
};

/**
 * Lấy icon component dựa trên đường dẫn
 */
export const getIconForPath = (path: string): React.FC<IconProps> => {
    return PATH_TO_ICON_MAP[path] || QuestionIcon;
};

/**
 * Lấy icon component dựa trên tên
 */
export const getIcon = <P extends object>(iconName: keyof typeof Icons, props?: P) => {
    const IconComponent = Icons[iconName];
    if (!IconComponent) {
        console.warn(`Icon "${iconName}" not found in MenuIcons.tsx`);
        return null;
    }
    return <IconComponent {...(props as any)} />;
};

export default Icons;
